import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CreativeMessageRole } from '@packages/global';
import { useCreativeChatStore } from '../../stores/creativeChatStore';
import { useToast } from '../../stores/toastStore';
import MessageBubble from './MessageBubble';
import ChatInput from './ChatInput';
import ModelSelector from './ModelSelector';
import MarkdownRenderer from '../common/MarkdownRenderer';
import {
  SparklesIcon,
  ChatBubbleLeftRightIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

const ChatArea: React.FC = () => {
  const { t } = useTranslation('creative');
  const toast = useToast();
  const {
    activeConversationId,
    messages,
    isGenerating,
    streamingContent,
    loadingMessages,
    conversations,
    sendMessage,
    deleteMessage,
    clearMessages,
    abortStream,
  } = useCreativeChatStore();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevConvIdRef = useRef<string | null>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  useEffect(() => {
    if (prevConvIdRef.current && prevConvIdRef.current !== activeConversationId) {
      abortStream();
    }
    prevConvIdRef.current = activeConversationId;
  }, [activeConversationId, abortStream]);

  useEffect(() => {
    return () => { abortStream(); };
  }, [abortStream]);

  const activeConversation = conversations.find((c) => c.id === activeConversationId);

  const handleClear = () => {
    clearMessages();
    toast.success(t('chat.clearSuccess'));
  };

  // 未选中会话
  if (!activeConversationId) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-bg-page">
        <ChatBubbleLeftRightIcon className="w-16 h-16 text-gray-500 mb-4" />
        <p className="text-gray-400 text-lg">{t('chat.emptyState')}</p>
        <p className="text-gray-500 text-sm mt-1">{t('chat.emptyHint')}</p>
      </div>
    );
  }

  const hasMessages = messages.length > 0 || isGenerating;

  return (
    <div className="flex-1 flex flex-col bg-bg-page min-w-0">
      {/* 顶部工具栏 */}
      <div className="flex items-center justify-between px-6 h-14 border-b border-border-subtle bg-bg-surface flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <ModelSelector />
          {activeConversation && (
            <span className="text-sm text-gray-400 font-medium truncate">
              {activeConversation.title || t('conversation.untitled')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isGenerating && (
            <button
              onClick={abortStream}
              className="text-xs text-gray-400 hover:text-error px-2 py-1 rounded transition-colors cursor-pointer"
            >
              {t('chat.stop')}
            </button>
          )}
          <button
            onClick={handleClear}
            disabled={!hasMessages || isGenerating}
            className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-bg-surface border border-border-subtle
                       text-gray-400 text-xs hover:text-gray-300 hover:border-border-default
                       disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            <TrashIcon className="w-3.5 h-3.5" />
            {t('chat.clear')}
          </button>
        </div>
      </div>

      {/* 消息列表 */}
      <div className="flex-1 overflow-y-auto px-12 py-6 space-y-6">
        {loadingMessages ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* 欢迎消息（无消息时显示） */}
            {messages.length === 0 && !isGenerating && (
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <SparklesIcon className="w-5 h-5 text-primary" />
                </div>
                <div className="max-w-[70%] bg-bg-surface rounded-2xl rounded-tl-sm px-4 py-3">
                  <MarkdownRenderer content={t('chat.welcome')} />
                </div>
              </div>
            )}
            {messages.map((msg) => (
              <MessageBubble key={msg.id} message={msg} onDelete={deleteMessage} />
            ))}
            {/* 流式输出中 */}
            {isGenerating && streamingContent && (
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <SparklesIcon className="w-5 h-5 text-primary" />
                </div>
                <div className="max-w-[70%] bg-bg-surface rounded-2xl rounded-tl-sm px-4 py-3">
                  <MarkdownRenderer content={streamingContent} />
                </div>
              </div>
            )}
            {/* 等待首个 token */}
            {isGenerating && !streamingContent && (
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <SparklesIcon className="w-5 h-5 text-primary" />
                </div>
                <div className="bg-bg-surface rounded-2xl rounded-tl-sm px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* 底部输入框 */}
      <ChatInput onSend={sendMessage} isGenerating={isGenerating} />
    </div>
  );
};

export default ChatArea;
